import React, { useEffect } from 'react';
import { Card, Button, Tag } from 'antd';
import { DownloadOutlined, LinkedinOutlined, MailOutlined, TrophyOutlined } from '@ant-design/icons';
import resumePDF from '../assets/AkhilMudetthula_T24developer.pdf';
import temenosLogo from '../assets/logos/temenos.png';
import '../Portfolio.css';
import { motion } from 'framer-motion';
import Hero3D from '../Hero3D';

const skills = [
  'JavaScript', 'Python', 'jBC', 'Java', 'infoBasic', 'React.js', 'Firebase', 'Postman',
  'Temenos T24 (AA, OFS, IRIS, COB, SECURITIES)', 'Eclipse', 'Quantum Visualizer', 'Git', 'Jira'
];

const awards = [
  {
    title: 'T-Star Collaborate Award – Q3 2022',
    description: 'For supporting maintenance team developers in enhancing T24 performance for AA module.'
  },
  {
    title: 'T-Star Commit Award – Q1 2023',
    description: 'For resolving performance issues in INTEREST property class during AMR rollout.'
  },
  {
    title: 'T-Star Commit Award – Q1 2024',
    description: 'For independently developing Asset Finance APIs, demonstrating strong ownership.'
  }
];

const experiences = [
  {
    title: 'Consultant',
    company: 'Capgemini',
    location: 'Hyderabad, TS',
    duration: 'June 2025 - Present',
    logo: temenosLogo,
    highlights: [
      'Delivered L3 customizations to meet complex client requirements, ensuring seamless integration with existing systems.',
      'Developed and optimized Java-based L3 routines to enhance functionality and performance for client-specific needs.',
      'Currently gaining expertise in Securities module and Temenos Payments Hub (TPH) to support future implementations and solution design.',
      
    ]
  },
  {
    title: 'Associate Product Engineer',
    company: 'Temenos',
    location: 'Chennai, TN',
    duration: 'Mar 2023 - June 2025',
    logo: temenosLogo,
    highlights: [
      'Led AA enhancements for key asset finance implementations, improving functionality and client-specific workflows.',
      'Built IRIS APIs for Asset Finance operations.',
      'Migrated contracts from non-AA to AA framework.',
      'Created dynamic pricing and payoff structures.',
      'Developed automation for asset lifecycle management.'
    ]
  },
  {
    title: 'Software Engineer',
    company: 'Temenos',
    location: 'Hyderabad, TG',
    duration: 'Nov 2021 - Mar 2023',
    logo: temenosLogo,
    highlights: [
      'Refactored AA routines for performance optimization.',
      'Enabled inline API simulations and reduced response time.',
      'Created optional currency and versioning frameworks.',
      'Worked with Regions Bank on tax performance enhancements.'
    ]
  }
];



const PortfolioPage = () => {
  return (
    <div className="portfolio fintech-theme">
      <motion.section 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 1 }} 
        className="hero-section"
      >
        <Hero3D />
        <div className="hero-content">
          <motion.h1 
            initial={{ y: -50, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ duration: 1 }}
          >
            Akhil Mudetthula
          </motion.h1>
          <motion.p 
            className="hero-subtitle"
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ duration: 1.2 }}
          >
            FinTech Engineer • T24 Developer • API Integrator
          </motion.p>
          <div className="hero-buttons">
            <Button shape="round" size="large" icon={<DownloadOutlined />} href={resumePDF} target="_blank">Download CV</Button>
            <Button shape="round" size="large" icon={<LinkedinOutlined />} href="https://www.linkedin.com/in/akhil-mudetthula/" target="_blank">LinkedIn</Button>
            <Button shape="round" size="large" icon={<MailOutlined />} href="mailto:akhilmudetthula@gmail.com">Email</Button>
          </div>
        </div>
      </motion.section>

      <motion.section className="split-section">
        <motion.div className="section-title" initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
          <h2>About</h2>
        </motion.div>
        <motion.div className="section-content" initial={{ x: 100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 1 }}>
          <p>
            I’m a FinTech-focused T24 Developer with 4+ years of experience building scalable banking solutions in the Temenos ecosystem.
            My work spans AA module customizations, OFS frameworks, and API integrations for global banks.
          </p>
        </motion.div>
      </motion.section>

      <motion.section className="split-section dark-block">
        <motion.div className="section-title" initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
          <h2>Skills</h2>
        </motion.div>
        <motion.div className="section-content skills-list" initial={{ x: 100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 1 }}>
          {skills.map((skill, index) => <Tag key={index} className="skill-tag">{skill}</Tag>)}
        </motion.div>
      </motion.section>

      <motion.section className="split-section">
        <motion.div className="section-title" initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
          <h2>Experience</h2>
        </motion.div>
        <motion.div className="section-content">
          {experiences.map((exp, idx) => (
            <Card key={idx} className="experience-card">
              <div className="experience-header">
                <img src={exp.logo} alt={exp.company} className="company-logo" />
                <div>
                  <h3>{exp.title} @ {exp.company}</h3>
                  <p className="meta">{exp.location} | {exp.duration}</p>
                </div>
              </div>
              <ul>
                {exp.highlights.map((point, i) => <li key={i}>{point}</li>)}
              </ul>
            </Card>
          ))}
        </motion.div>
      </motion.section>

      <motion.section className="split-section dark-block">
        <motion.div className="section-title" initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
          <h2>Recognitions</h2>
        </motion.div>
        <motion.div className="section-content recognitions" initial={{ x: 100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 1 }}>
          {awards.map((award, idx) => (
            <Card key={idx} className="recognition-card">
              <div className="award-title">🏅 {award.title}</div>
              <p className="award-desc">{award.description}</p>
            </Card>
          ))}
        </motion.div>
      </motion.section>
    </div>
  );
};

export default PortfolioPage;
